import { rolleZaehler } from '@theme/dp-zaehler-rolle';
import { tauscheBildWeich } from '@theme/dp-bildtausch';

/**
 * Schreibt Fokus und Zoom an ein Kachelbild - als Variablen und Klasse fuer
 * das Stilblatt (.dp-zoomt vergroessert ohne transform um den Fokus).
 *
 * @param {HTMLImageElement} bild
 * @param {string} fokus - "x% y%" oder leer
 * @param {string} zoom - Faktor oder leer
 */
function setzeFokusUndZoom(bild, fokus, zoom) {
  const ziel = fokus || '';
  if (bild.style.objectPosition !== ziel) bild.style.objectPosition = ziel;
  const teile = ziel.split(' ');
  bild.style.setProperty('--dp-fokus-x', teile[0] || '');
  bild.style.setProperty('--dp-fokus-y', teile[1] || '');
  bild.style.setProperty('--dp-zoom', zoom || '');
  bild.classList.toggle('dp-zoomt', !!zoom);
}

/**
 * Bilder auf den Produktkacheln durchblaettern.
 *
 * Das Bild folgt dem Finger und rastet beim Loslassen auf das naechste oder
 * vorige ein; ein Zaehler in der Bildecke sagt, wo man ist. Die Kachel bleibt
 * dabei ein Verweis - ein Wischen darf ihn nur nicht ausloesen.
 *
 * Gezeigt werden immer nur drei Felder: das vorige, das aktuelle und das
 * naechste. Die Bahn steht in Ruhe auf dem mittleren; nach jedem Einrasten
 * werden die drei neu befuellt und die Bahn ohne Uebergang zurueckgesetzt.
 * So bleibt der Weg immer gleich kurz, das Blaettern laeuft im Kreis, und je
 * Kachel liegen nie mehr als drei Bilder im Speicher.
 *
 * Die Bilder stehen als Liste im Markup (data-dp-bilder in der Kachel), damit
 * der Browser nichts nachladen muss, um die Reihenfolge zu kennen.
 */

/** Ab dieser Strecke gilt eine Geste als Wischen. */
const SCHWELLE = 40;

/** Ein kurzer Stupser zaehlt auch unterhalb der Schwelle. */
const SCHNELL = 250;
const STUPSER = 12;

/** So lange dauert das Einrasten - im Gleichklang mit der CSS-Angabe. */
const GLEITZEIT = 340;

/**
 * Zeitachse des Wischhinweises - dieselbe wie bei den uebrigen Hinweisen im
 * Shop: bei 18 Prozent setzt sich der Punkt in Bewegung, bei 48 ist er am
 * weitesten, bei 78 wieder am Platz. Der Rest der Zeit gehoert dem Ausblenden.
 */
const WISCH_DAUER = 2200;
const WISCH_WEG = 72;
const WISCH_BEGINN = 0.18;
const WISCH_OBEN = 0.48;
const WISCH_ENDE = 0.78;

/** Wie oft ein Hinweis je Besuch hoechstens erscheint. */
const HINWEIS_HOECHSTENS = 2;

/**
 * Darf dieser Hinweis noch einmal gezeigt werden?
 *
 * Hoechstens zweimal je Besuch - danach kennt man ihn. Gezaehlt wird im
 * Sitzungsspeicher: er gilt fuer den Tab und ist beim naechsten Besuch wieder
 * leer. Dieselbe Zaehlung steht in dp-bild-overlay.js, dp-galerie-hinweis.js
 * und dp-scroller.js; jede fuehrt ihren eigenen Eintrag.
 *
 * Gezaehlt wird erst beim tatsaechlichen Zeigen, nicht beim Pruefen der
 * uebrigen Bedingungen.
 *
 * @returns {boolean}
 */
function darfHinweisZeigen() {
  const schluessel = 'dp-hinweis-kachel';

  try {
    const bisher = Number(window.sessionStorage.getItem(schluessel) || '0');
    if (bisher >= HINWEIS_HOECHSTENS) return false;

    window.sessionStorage.setItem(schluessel, String(bisher + 1));
    return true;
  } catch {
    /*
     * Im privaten Fenster ist der Speicher gesperrt. Dann eben ohne Zaehlung -
     * ein Hinweis ist kein Grund, hier auszusteigen.
     */
    return true;
  }
}

/**
 * Weiches An- und Auslaufen.
 *
 * @param {number} t Anteil zwischen 0 und 1.
 * @returns {number}
 */
function weich(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Steht die Flaeche vollstaendig im Bild?
 *
 * @param {Element} flaeche
 * @returns {boolean}
 */
function ganzImBild(flaeche) {
  const rand = flaeche.getBoundingClientRect();
  const hoehe = window.innerHeight || document.documentElement.clientHeight;

  return rand.top >= 0 && rand.bottom <= hoehe;
}

/**
 * Richtet eine Kachel ein.
 *
 * @param {HTMLElement} kachel
 */
function richteKachelEin(kachel) {
  if (kachel.dataset.dpBlaettert !== undefined) return;

  const daten = kachel.querySelector('[data-dp-bilder]');
  const basis = kachel.querySelector('.resource-card__image:not(.resource-card__image--secondary)');
  const medien = kachel.querySelector('.resource-card__media');
  if (!daten || !basis || !medien) return;

  let bilder = [];
  try {
    bilder = JSON.parse(daten.textContent);
  } catch {
    return;
  }
  if (bilder.length < 1) return;

  /*
   * Die Fokuspunkte, stellengleich zur Bilderliste - leer heisst Mitte.
   * Beim Tausch eines Bildes wandert sein Fokus als object-position mit,
   * damit der Beschnitt (object-fit: cover) um den Fokus rueckt.
   */
  let fokusse = [];
  try {
    const fokusDaten = kachel.querySelector('[data-dp-fokusse]');
    if (fokusDaten) fokusse = JSON.parse(fokusDaten.textContent);
  } catch {
    fokusse = [];
  }

  /** @param {string} quelle @returns {string} */
  const fokusVon = (quelle) => fokusse[bilder.indexOf(quelle)] || '';

  /*
   * Die Zoomfaktoren (Alt-Marke "zoom:XX"), stellengleich zur Bilderliste -
   * die Kachel vergroessert das Bild um seinen Fokus (divineprint.css).
   */
  let zooms = [];
  try {
    const zoomDaten = kachel.querySelector('[data-dp-zooms]');
    if (zoomDaten) zooms = JSON.parse(zoomDaten.textContent);
  } catch {
    zooms = [];
  }
  /** @param {string} quelle @returns {string} */
  const zoomVon = (quelle) => zooms[bilder.indexOf(quelle)] || '';

  /**
   * Setzt Quelle, Fokus und Zoom eines Bahnfeldes - nur bei Abweichung.
   *
   * @param {HTMLImageElement} feld
   * @param {string} quelle
   * @param {string} fokus
   */
  const setzeFeld = (feld, quelle, fokus) => {
    const ziel = skaliere(quelle);
    if (feld.getAttribute('src') !== ziel) feld.src = ziel;
    setzeFokusUndZoom(feld, fokus, zoomVon(quelle));
  };

  /*
   * Die Bahn ist nur Beiwerk: in Ruhe zeigt das Kartenbild (resource-card__image,
   * gleiches Bild, gleicher Fokus und Zoom) dasselbe. Deshalb bekommen die drei
   * Felder erst eine Quelle, wenn die Karte sichtbar ist und gewischt werden
   * koennte (aktiviere), und geben sie wieder her, sobald die Karte weit aus
   * dem Bild und noch unberuehrt ist (deaktiviere). Vorher lud jede Karte -
   * auch in zugeklappten Menues und weit rechts im Slider - drei Bilder in
   * 800 Pixeln Breite: auf der Produktseite gut 80 MB entpackte Bildmasse.
   */
  let aktiv = false;
  let veraendert = false;

  /**
   * Die Adresse in der Breite, die die Flaeche braucht (Anzeige x Punktdichte
   * x Zoom) in Hunderterschritten, nie ueber der Breite der Liste.
   *
   * @param {string} quelle
   * @returns {string}
   */
  const skaliere = (quelle) => {
    const breit = medien.clientWidth;
    if (!breit || !/[?&]width=\d+/.test(quelle)) return quelle;
    const zoom = Number(zoomVon(quelle)) || 1;
    const noetig = breit * (window.devicePixelRatio || 1) * Math.max(1, zoom);
    const ziel = Math.min(800, Math.max(300, Math.ceil(noetig / 100) * 100));
    return quelle.replace(/([?&]width=)\d+/, '$1' + ziel);
  };

  kachel.dataset.dpBlaettert = '';

  /*
   * Der Zaehler wird in die Bildflaeche gehaengt. Im Markup steht er daneben -
   * in die Karte hineinschreiben laesst sich von aussen nichts, und ein
   * eigener Kasten um das Bild wuerde dessen Rundung stoeren.
   */
  const zaehler = kachel.querySelector('[data-dp-zaehler]');
  if (zaehler) medien.appendChild(zaehler);

  /*
   * Die Hoehe der Kachel steht fest und bleibt es auch beim Blaettern.
   *
   * Das Mass kommt aus dem Markup: die Karte schreibt ihr Seitenverhaeltnis
   * selbst an die Bildflaeche - fest eingestellt (4/5 in den Slidern und der
   * Suche) oder als Verhaeltnis des ersten Bildes, wo die Kacheln versetzt
   * stehen. Beides gilt unangetastet: jede Kachel behaelt ihre Groesse, egal
   * welches Format das gerade gezeigte Bild hat - die Bilder fuellen die
   * Flaeche und werden beschnitten (object-fit: cover).
   *
   * Frueher wurde hier das Verhaeltnis des ersten Bildes darueber
   * geschrieben. Damit wichen Kacheln mit festem Mass wieder voneinander ab,
   * und das Overlay lag auf lauter verschieden hohen Feldern. Nur wenn das
   * Markup gar kein Mass mitbringt, springt die Messung ein.
   */
  const halteVerhaeltnis = () => {
    if (medien.style.getPropertyValue('--resource-card-aspect-ratio').trim() !== '') return;
    if (!basis.naturalWidth || !basis.naturalHeight) return;
    medien.style.setProperty(
      '--resource-card-aspect-ratio',
      (basis.naturalWidth / basis.naturalHeight).toFixed(4)
    );
  };
  if (basis.complete) halteVerhaeltnis();
  else basis.addEventListener('load', halteVerhaeltnis, { once: true });

  /* ---------------------------------------------------------------- Bahn */

  const bahn = document.createElement('div');
  bahn.className = 'dp-bahn';

  /*
   * Jedes Bild in einer eigenen Huelle: sie ist das Glied der Bahn und der
   * Kasten, in dem ein vergroessertes Bild (dp-zoomt) absolut liegt und
   * beschnitten wird.
   */
  const felder = [0, 1, 2].map(() => {
    const huelle = document.createElement('div');
    huelle.className = 'dp-bahn__feld';
    const feld = document.createElement('img');
    feld.className = 'dp-bahn__bild';
    feld.alt = '';
    feld.decoding = 'async';
    feld.setAttribute('aria-hidden', 'true');
    huelle.appendChild(feld);
    bahn.appendChild(huelle);
    return feld;
  });

  medien.classList.add('dp-medien--bahn');
  medien.appendChild(bahn);

  /** Ob die Kachel gerade mehr als ein Bild zum Wischen hat (fuer den Wischhinweis). */
  kachel.dpWischbar = () => bilder.length > 1;

  let index = 0;

  const bei = (nummer) => bilder[((nummer % bilder.length) + bilder.length) % bilder.length];

  const fuelle = () => {
    if (!aktiv) return;
    const gewuenscht = [bei(index - 1), bei(index), bei(index + 1)];
    felder.forEach((feld, stelle) => {
      setzeFeld(feld, gewuenscht[stelle], fokusVon(gewuenscht[stelle]));
    });
  };

  const ruhe = () => {
    bahn.style.transform = 'translate3d(-100%, 0, 0)';
  };

  /**
   * @param {'hoch' | 'runter'} [richtung] Rollrichtung der Ziffern; ohne
   *   Angabe steht die Zahl sofort da.
   */
  const zeigeZaehler = (richtung) => {
    if (!zaehler) return;
    // Mit einem einzigen Bild gibt es nichts zu zaehlen: der Zaehler blendet aus, mit mehreren wieder ein.
    zaehler.style.transition = 'opacity 0.25s ease';
    zaehler.style.opacity = bilder.length < 2 ? '0' : '';
    rolleZaehler(zaehler, index + 1 + '/' + bilder.length, richtung || 'hoch', !richtung);
  };

  /** Legt die Bilder in die Bahn - ab jetzt ist die Karte wischbereit. */
  const aktiviere = () => {
    if (aktiv) return;
    aktiv = true;
    fuelle();
  };

  /** Gibt die Bilder der Bahn frei - nur solange die Karte unberuehrt ist. */
  const deaktiviere = () => {
    if (!aktiv || veraendert || abschluss || start) return;
    aktiv = false;
    for (const feld of felder) feld.removeAttribute('src');
  };

  /** Wartet, bis die Felder geladen sind - hoechstens 1,5 Sekunden. */
  const warteAufFelder = () =>
    Promise.race([
      Promise.all(
        felder.map((feld) =>
          feld.getAttribute('src') && !feld.complete
            ? new Promise((fertig) => {
                feld.addEventListener('load', fertig, { once: true });
                feld.addEventListener('error', fertig, { once: true });
              })
            : Promise.resolve()
        )
      ),
      new Promise((fertig) => window.setTimeout(fertig, 1500)),
    ]);

  ruhe();
  zeigeZaehler();

  /**
   * Setzt die Bahn ohne Uebergang auf ein bestimmtes Bild.
   *
   * @param {number} stelle
   */
  const springe = (stelle) => {
    index = ((stelle % bilder.length) + bilder.length) % bilder.length;
    veraendert = true;
    aktiv = true;
    bahn.classList.remove('dp-bahn--gleitet');
    fuelle();
    ruhe();
    zeigeZaehler();
  };

  /*
   * Solange das Einrasten laeuft, wartet hier der Abschluss. Faengt jemand
   * mittendrin eine neue Geste an, wird er vorgezogen, damit die Bahn nicht
   * aus einer halben Bewegung heraus weiterlaeuft.
   */
  let abschluss = null;

  /**
   * Laesst die Bahn um einen Schritt weitergleiten (0 = zurueck an den Platz).
   *
   * @param {number} schritt
   */
  const gleite = (schritt) => {
    veraendert = true;
    bahn.classList.add('dp-bahn--gleitet');
    bahn.style.transform = 'translate3d(' + (-100 - schritt * 100) + '%, 0, 0)';

    if (schritt) {
      index = (((index + schritt) % bilder.length) + bilder.length) % bilder.length;
      zeigeZaehler(schritt > 0 ? 'hoch' : 'runter');
    }

    let erledigt = false;
    const beenden = () => {
      if (erledigt) return;
      erledigt = true;
      if (abschluss === beenden) abschluss = null;

      bahn.classList.remove('dp-bahn--gleitet');
      fuelle();
      ruhe();
    };
    abschluss = beenden;

    bahn.addEventListener('transitionend', beenden, { once: true });

    /*
     * Faellt der Uebergang aus - etwa weil die Kachel gerade nicht sichtbar
     * ist -, kaeme kein transitionend. Deshalb ein Netz darunter.
     */
    window.setTimeout(beenden, GLEITZEIT + 80);
  };

  /* ---------------------------------------------------- Bildwunsch von aussen */

  /*
   * Die Farbpunkte der Kachel melden hier, welches Bild gezeigt werden soll
   * (dp-farbpunkte.js schickt die Adresse). Die Bahn gleitet dann zum
   * passenden Bild - in die kuerzere Richtung, mit derselben Bewegung wie
   * beim Wischen. Kennt die Liste die Adresse nicht - so ist es bei jedem
   * Bild mit Farb-Marke, denn geblaettert wird nur durch die Bilder ohne
   * Marke (dp-kachel-inhalt) -, wechselt das aktuelle Feld an Ort und
   * Stelle: weich uebergeblendet ueber denselben Baustein wie das
   * Kartenbild (dp-bildtausch.js).
   */
  /*
   * Die Bilder je Farbe (data-dp-farblisten, dp-kachel-inhalt): Mit dem Tipp auf
   * einen Farbpunkt blaettert die Bahn ab jetzt durch die Bilder dieser Farbe -
   * ihr Hauptbild vorn, dahinter die Bilder mit "farbe:<wert>, kachel". Wird
   * der Punkt abgewaehlt, gilt wieder die Liste ohne Farbwahl. Bliebe die
   * Standardliste stehen, verschwaende das Farbbild beim ersten Wischen.
   */
  let farblisten = {};
  try {
    const farbDaten = kachel.querySelector('[data-dp-farblisten]');
    if (farbDaten) farblisten = JSON.parse(farbDaten.textContent) || {};
  } catch {
    farblisten = {};
  }
  const standard = { bilder: bilder.slice(), fokusse: fokusse.slice(), zooms: zooms.slice() };
  let aktuelleFarbe = '';
  // Eine Farbe ohne Bild: die Bahn ist abgeblendet, der Hinweis "Noch keine Bilder" steht darunter.
  let bahnLeer = false;

  /** Dieselbe Datei, egal in welcher Breite sie gerade angefordert ist. */
  const ohneAnfrage = (adresse) => String(adresse).split('?')[0];

  /**
   * Stellt die Bahn auf die Bilder einer Farbe um - ohne Farbe: auf die
   * Standardliste. Das gezeigte Bild blendet weich ins neue; nur die
   * Nachbarfelder, die nicht zu sehen sind, werden still neu belegt.
   *
   * @param {string} farbe - leer: Farbe abgewaehlt
   * @param {string} quelle - Das Bild, das der Punkt zeigt.
   * @param {string} fokus
   * @param {string} zoom
   * @returns {boolean} Ob die Liste gewechselt wurde.
   */
  const wechsleListe = (farbe, quelle, fokus, zoom) => {
    let neu = standard.bilder;
    let neuFokus = standard.fokusse;
    let neuZoom = standard.zooms;
    let stelle = 0;

    if (farbe) {
      const eintraege = farblisten[farbe];
      if (!Array.isArray(eintraege) || !eintraege.length) return false;
      neu = eintraege.map((e) => e.b);
      neuFokus = eintraege.map((e) => e.f || '');
      neuZoom = eintraege.map((e) => e.z || '');
      // Das Bild des Punktes ist das Hauptbild und steht vorn; sonst kommt es vorne dazu.
      stelle = neu.findIndex((adresse) => ohneAnfrage(adresse) === ohneAnfrage(quelle));
      if (stelle < 0) {
        neu = [quelle, ...neu];
        neuFokus = [fokus || '', ...neuFokus];
        neuZoom = [zoom || '', ...neuZoom];
        stelle = 0;
      }
    } else {
      fokus = neuFokus[0] || '';
      zoom = neuZoom[0] || '';
    }

    if (abschluss) abschluss();

    const warLeer = bahnLeer;
    bahnLeer = false;
    bilder = neu;
    fokusse = neuFokus;
    zooms = neuZoom;
    index = stelle;
    aktuelleFarbe = farbe;

    setzeFeld(felder[0], bei(index - 1), fokusVon(bei(index - 1)));
    setzeFeld(felder[2], bei(index + 1), fokusVon(bei(index + 1)));
    if (warLeer) {
      // Die Bahn stand abgeblendet: Das neue Bild liegt sofort bereit, sie blendet damit auf.
      setzeFeld(felder[1], bei(index), fokusVon(bei(index)));
      bahn.classList.remove('dp-bahn--leer');
    } else {
      tauscheBildWeich(felder[1], bei(index), (feld) => setzeFokusUndZoom(feld, fokus, zoom));
    }
    zeigeZaehler('hoch');
    return true;
  };

  kachel.addEventListener('dp:zeigeBild', (ereignis) => {
    // Eine Farbe ohne Bild: die Bahn blendet ab, der Hinweis darunter wird frei.
    if (ereignis instanceof CustomEvent && ereignis.detail && ereignis.detail.leer) {
      if (abschluss) abschluss();
      aktuelleFarbe = ereignis.detail.farbe || '';
      bahnLeer = true;
      bahn.classList.add('dp-bahn--leer');
      if (zaehler) {
        zaehler.style.transition = 'opacity 0.25s ease';
        zaehler.style.opacity = '0';
      }
      return;
    }

    const quelle = ereignis instanceof CustomEvent && ereignis.detail ? ereignis.detail.quelle : '';
    if (!quelle) return;

    veraendert = true;
    if (!aktiv) {
      aktiviere();
      warteAufFelder().then(() => zeigeBild(ereignis, quelle));
      return;
    }
    zeigeBild(ereignis, quelle);
  });


  /**
   * @param {Event} ereignis
   * @param {string} quelle
   */
  const zeigeBild = (ereignis, quelle) => {
    const farbe = ereignis.detail && ereignis.detail.farbe ? ereignis.detail.farbe : '';
    const zurueck = !!(ereignis.detail && ereignis.detail.zuruecksetzen);
    if (zurueck || (farbe && farbe !== aktuelleFarbe)) {
      const f = ereignis.detail.fokus || '';
      const z = ereignis.detail.zoom || '';
      if (wechsleListe(zurueck ? '' : farbe, quelle, f, z)) return;
    }
    // Die Bahn stand abgeblendet, die Farbe hat aber keine eigene Liste: Sie blendet wieder auf.
    if (bahnLeer) {
      bahnLeer = false;
      bahn.classList.remove('dp-bahn--leer');
      zeigeZaehler();
    }
    const ziel = bilder.indexOf(quelle);

    if (ziel < 0) {
      const fremdFokus = ereignis.detail && ereignis.detail.fokus ? ereignis.detail.fokus : '';
      const fremdZoom = ereignis.detail && ereignis.detail.zoom ? ereignis.detail.zoom : '';
      tauscheBildWeich(felder[1], quelle, (feld) => setzeFokusUndZoom(feld, fremdFokus, fremdZoom));
      return;
    }
    if (ziel === index) return;

    // Laeuft noch ein Einrasten, wird es vorgezogen - wie bei einer neuen Geste.
    if (abschluss) abschluss();

    const vorwaerts = (ziel - index + bilder.length) % bilder.length;
    const schritt = vorwaerts <= bilder.length / 2 ? 1 : -1;

    // Das Ziel als Nachbarn bereitlegen, dann einen Schritt hinuebergleiten.
    index = ((ziel - schritt) % bilder.length + bilder.length) % bilder.length;
    fuelle();
    ruhe();
    void bahn.offsetWidth;
    gleite(schritt);
  };

  /* --------------------------------------------------------------- Geste */

  /*
   * Abgenommen wird die Geste an der Karte, nicht an der Bildflaeche.
   *
   * Ueber dem Bild liegt der Verweis der Karte als eigene Flaeche - ein Finger
   * trifft immer ihn, nie das Bild darunter. An der Bildflaeche kaeme deshalb
   * kein einziges Ereignis an; am gemeinsamen Elternteil kommen beide vorbei.
   */
  const karte = kachel.querySelector('.dp-karte--eigen') || kachel;

  /*
   * Steht die Kachel in einem Slider, wischt der Finger den Slider - nicht
   * die Bilder darin. Zwei Wischrichtungen auf derselben Flaeche, beide
   * waagrecht, kann niemand auseinanderhalten: die Bahn fing die Geste ab und
   * der Slider blieb stehen. Mit der Maus bleibt das Blaettern erlaubt, sie
   * scrollt den Slider ja nicht durch Ziehen.
   */
  const imSlider = !!kachel.closest('.dp-scroller, .dp-sub-brand__scroller');

  /*
   * Wann die Bahn ihre Bilder bekommt: ein Zeiger (Maus, Finger) ueber der
   * Karte genuegt; wo der Finger wischt - Raster, kein Slider -, schon wenn
   * die Karte in die Naehe des Bildes kommt, damit das erste Wischen nicht auf
   * ein Bild wartet. Weit aus dem Bild und unberuehrt gibt sie sie zurueck.
   * Karten in zugeklappten Menues sind nie im Bild und laden nichts.
   */
  karte.addEventListener('pointerenter', aktiviere, { passive: true });
  karte.addEventListener('pointerdown', aktiviere, { passive: true });

  const wischtMitFinger = !imSlider && window.matchMedia('(pointer: coarse)').matches;
  if (typeof IntersectionObserver === 'function') {
    new IntersectionObserver(
      (eintraege) => {
        for (const eintrag of eintraege) {
          if (eintrag.isIntersecting) {
            if (wischtMitFinger) aktiviere();
          } else {
            deaktiviere();
          }
        }
      },
      { rootMargin: '300px 100px' }
    ).observe(medien);
  } else {
    aktiviere();
  }

  let start = null;
  let zieht = false;
  let gewischt = false;
  /*
   * Die letzte bekannte Fingerposition. pointercancel - auf iOS der uebliche
   * Abschluss, sobald Safari die Beruehrung selbst deuten will - traegt keine
   * brauchbaren Koordinaten mehr (clientX ist dann 0). Gerechnet wird deshalb
   * mit der letzten Bewegung, nicht mit dem Abschlussereignis.
   */
  let letzteX = 0;

  karte.addEventListener(
    'pointerdown',
    (ereignis) => {
      // Ein Farbpunkt ist kein Wischen.
      if (ereignis.target instanceof Element && ereignis.target.closest('.dp-farbe')) return;

      // Im Slider gehoert der Fingerwisch dem Slider.
      if (imSlider && ereignis.pointerType === 'touch') return;

      // Mit einem einzigen Bild (bis eine Farbe mehr mitbringt) gibt es nichts zu wischen.
      if (bilder.length < 2 || bahnLeer) return;

      if (abschluss) abschluss();

      start = {
        x: ereignis.clientX,
        y: ereignis.clientY,
        zeit: performance.now(),
        zeiger: ereignis.pointerId,
      };
      letzteX = ereignis.clientX;
      zieht = false;
      gewischt = false;
    },
    { passive: true }
  );

  karte.addEventListener(
    'pointermove',
    (ereignis) => {
      if (!start) return;

      const weg = ereignis.clientX - start.x;
      const hoch = ereignis.clientY - start.y;
      letzteX = ereignis.clientX;

      /*
       * Erst wenn die Bewegung eindeutig waagrecht ist, uebernimmt die Bahn.
       * Vorher koennte noch das Scrollen der Seite gemeint sein.
       */
      if (!zieht) {
        if (Math.abs(weg) < 8 || Math.abs(weg) <= Math.abs(hoch)) return;
        zieht = true;
        gewischt = true;
        bahn.classList.remove('dp-bahn--gleitet');
        try {
          karte.setPointerCapture(start.zeiger);
        } catch {
          /* Nicht jeder Zeiger laesst sich einfangen - dann eben ohne. */
        }
      }

      bahn.style.transform = 'translate3d(calc(-100% + ' + weg.toFixed(1) + 'px), 0, 0)';
    },
    { passive: true }
  );

  const loslassen = (ereignis) => {
    if (!start) return;

    /*
     * pointercancel meldet 0/0 - die Richtung kaeme dann immer als "weit nach
     * links" heraus, und die Bilder wischten unabhaengig von der Geste stets
     * in dieselbe Richtung. Massgeblich ist die letzte echte Bewegung.
     */
    const dortX =
      ereignis.type === 'pointercancel' || typeof ereignis.clientX !== 'number' || ereignis.clientX === 0
        ? letzteX
        : ereignis.clientX;
    const weg = dortX - start.x;
    const dauer = performance.now() - start.zeit;
    const gezogen = zieht;

    start = null;
    zieht = false;

    if (!gezogen) return;

    // Ein kurzer, entschiedener Stupser genuegt auch ohne die volle Strecke.
    const weit = Math.abs(weg) > SCHWELLE || (dauer < SCHNELL && Math.abs(weg) > STUPSER);
    gleite(weit ? (weg < 0 ? 1 : -1) : 0);
  };

  karte.addEventListener('pointerup', loslassen, { passive: true });
  karte.addEventListener('pointercancel', loslassen, { passive: true });

  /*
   * Nach einem Wischen darf der Verweis nicht ausloesen. Der Klick kommt
   * unmittelbar danach, deshalb genuegt ein Merker, der gleich wieder faellt.
   */
  kachel.addEventListener(
    'click',
    (ereignis) => {
      if (!gewischt) return;

      ereignis.preventDefault();
      ereignis.stopPropagation();
      gewischt = false;
    },
    true
  );

  /*
   * Waehlt jemand eine Farbe, springt die Bahn auf das Bild dieser Farbe -
   * ohne Uebergang, denn es ist keine Blaetterbewegung, sondern eine Wahl.
   */
  kachel.addEventListener(
    'click',
    (ereignis) => {
      const punkt = ereignis.target instanceof Element ? ereignis.target.closest('.dp-farbe') : null;
      if (!punkt || !punkt.dataset.dpBild) return;

      window.setTimeout(() => {
        const stelle = bilder.findIndex((adresse) => gleichesBild(adresse, punkt.dataset.dpBild));
        if (stelle >= 0) springe(stelle);
      }, 0);
    },
    true
  );

  /* ------------------------------------------------------------- Hinweis */

  /**
   * Deutet einmal an, dass sich hier ein Bild weiterwischen laesst.
   *
   * Ein Punkt am rechten Bildrand wischt nach links, die Bahn laeuft dieselbe
   * Strecke mit und gibt das naechste Bild ein Stueck weit frei - dieselbe
   * Sprache wie der Hinweis auf der Bildergalerie und beim Pinch-to-Zoom.
   *
   * Beides kommt aus einer einzigen Schleife: ein Wert je Bild, zweifach
   * verwendet. Zwei getrennte Animationen liefen nie ganz gleich.
   *
   * @returns {boolean} Ob der Hinweis tatsaechlich laeuft.
   */
  kachel.dpZeigeWischhinweis = () => {
    // Nur dort, wo gewischt wird - am Rechner fuehrt der Zeiger.
    if (window.matchMedia('(min-width: 750px)').matches) return false;

    // Im Slider gibt es die Geste nicht - dann auch keine Vorfuehrung.
    if (imSlider) return false;
    if (bilder.length < 2 || bahnLeer) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    // Wer schon selbst geblaettert hat, braucht die Vorfuehrung nicht mehr.
    if (gewischt || index !== 0) return false;

    const weg = Math.min(WISCH_WEG, Math.round(medien.clientWidth * 0.35));
    if (weg < 24) return false;

    if (!darfHinweisZeigen()) return false;

    aktiviere();

    const hinweis = document.createElement('span');
    hinweis.className = 'dp-wischhinweis dp-wischhinweis--kachel';
    hinweis.setAttribute('aria-hidden', 'true');
    hinweis.innerHTML = '<span class="dp-wischhinweis__punkt"></span>';
    medien.appendChild(hinweis);

    const punkt = hinweis.firstElementChild;
    let abgebrochen = false;

    const beenden = () => {
      abgebrochen = true;
      hinweis.remove();
    };

    // Wer selbst zugreift, unterbricht die Vorfuehrung.
    const abbrechen = () => {
      if (abgebrochen) return;
      beenden();
      ruhe();
    };
    for (const art of ['pointerdown', 'touchstart']) {
      karte.addEventListener(art, abbrechen, { passive: true, once: true });
    }

    // Abgeraeumt wird erst, wenn auch das Ausblenden durch ist.
    window.setTimeout(beenden, WISCH_DAUER);

    const beginn = performance.now();

    const schritt = (jetzt) => {
      if (abgebrochen) return;

      const t = (jetzt - beginn) / WISCH_DAUER;

      if (t >= WISCH_ENDE) {
        ruhe();

        /*
         * Hier endet nur die Bewegung, nicht der Hinweis: das Ausblenden
         * laeuft noch bis zum Ende der Zeitachse. Wer den Punkt an dieser
         * Stelle entfernt, schneidet es ab und er verschwindet schlagartig.
         */
        return;
      }

      let weite = 0;
      if (t > WISCH_BEGINN && t <= WISCH_OBEN) {
        weite = weg * weich((t - WISCH_BEGINN) / (WISCH_OBEN - WISCH_BEGINN));
      } else if (t > WISCH_OBEN) {
        weite = weg * (1 - weich((t - WISCH_OBEN) / (WISCH_ENDE - WISCH_OBEN)));
      }

      bahn.style.transform = 'translate3d(calc(-100% - ' + weite.toFixed(1) + 'px), 0, 0)';
      punkt.style.transform = 'translateX(' + -weite.toFixed(1) + 'px)';

      window.requestAnimationFrame(schritt);
    };

    window.requestAnimationFrame(schritt);
    return true;
  };
}

/**
 * Zwei Bildadressen zeigen dasselbe Bild, wenn ihr Pfad uebereinstimmt - die
 * Groessenangabe dahinter unterscheidet sich haeufig.
 *
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function gleichesBild(a, b) {
  return a.split('?')[0] === b.split('?')[0];
}

/** Der Hinweis wird je Seitenaufruf nur einmal vergeben. */
let hinweisVergeben = false;

function starte() {
  for (const kachel of document.querySelectorAll('.dp-product:has([data-dp-bilder])')) {
    richteKachelEin(kachel);
  }

  if (!hinweisVergeben) deuteWischenAn();
}

/**
 * Gezeigt wird der Hinweis nur an der ersten Kachel - einmal verstanden, gilt
 * er fuer alle. Und erst, wenn sie vollstaendig im Bild steht: eine halb
 * abgeschnittene Vorfuehrung erklaert nichts.
 */
function deuteWischenAn() {
  const kachel = [...document.querySelectorAll('.dp-product:has([data-dp-bilder])')].find(
    (k) => typeof k.dpWischbar === 'function' && k.dpWischbar()
  );
  const medien = kachel?.querySelector('.resource-card__media');
  if (!kachel || !medien || typeof kachel.dpZeigeWischhinweis !== 'function') return;

  hinweisVergeben = true;

  let geplant = false;

  const blick = new IntersectionObserver(
    (eintraege) => {
      for (const eintrag of eintraege) {
        if (!eintrag.isIntersecting || geplant) continue;

        /*
         * Einen Moment warten, damit die Kachel erst zur Ruhe kommt - und
         * danach noch einmal nachsehen: in der Zwischenzeit kann sie wieder
         * aus dem Bild gescrollt sein.
         */
        geplant = true;
        window.setTimeout(() => {
          geplant = false;

          if (!ganzImBild(medien)) return;
          if (kachel.dpZeigeWischhinweis()) blick.disconnect();
        }, 700);
      }
    },
    { threshold: 1 }
  );

  blick.observe(medien);
}

/*
 * Beim Nachladen weiterer Artikel kommen neue Kacheln dazu - der Beobachter
 * meldet sie.
 */
/*
 * Die Livesuche tauscht den ganzen Trefferabschnitt aus - mit ihm verschwindet
 * das Raster, an dem der Beobachter haengt. Sie meldet sich deshalb, sobald
 * neue Kacheln stehen.
 */
document.addEventListener('dp:nachschub', () => {
  starte();
  beobachteNachschub();
});

function beobachteNachschub() {
  /*
   * Zwei Stellen liefern Kacheln nach: das Raster einer Kategorie beim
   * Weiterblaettern und die Vorschlaege der Suche bei jeder Eingabe.
   */
  const nachschub = document.querySelectorAll('.product-grid, predictive-search-component');

  for (const bereich of nachschub) {
    // Nach einem Austausch waere der Bereich sonst mehrfach beobachtet.
    if (bereich.dataset.dpBeobachtet !== undefined) continue;
    bereich.dataset.dpBeobachtet = '';

    const waechter = new MutationObserver(() => starte());
    waechter.observe(bereich, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      starte();
      beobachteNachschub();
    },
    { once: true }
  );
} else {
  starte();
  beobachteNachschub();
}
